import { DragDropProvider, type DragEndEvent } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { GripVertical } from "lucide-react";
import * as React from "react";

import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import {
  getSortableListReorder,
  hasSortableIndices,
  reorderSortableListItems,
} from "./SortableList.helpers";
import type {
  SortableListItemId,
  SortableListProps,
  SortableListRenderItemProps,
} from "./SortableList.types";

export const SortableList = <Item,>({
  items,
  getItemId,
  getItemLabel,
  renderItem,
  onItemsChange,
  onReorder,
  listLabel,
  disabled = false,
  itemClassName,
  handleClassName,
  getHandleLabel,
  className,
  "aria-label": ariaLabel,
  ...props
}: SortableListProps<Item>) => {
  const itemIds = React.useMemo(
    () => items.map((item) => getItemId(item)),
    [getItemId, items],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) {
        return;
      }

      const { source, target } = event.operation;

      if (!source || !target) {
        return;
      }

      const reorder = getSortableListReorder(
        itemIds,
        source.id as SortableListItemId,
        target.id as SortableListItemId,
        hasSortableIndices(source) ? source : undefined,
      );

      if (!reorder) {
        return;
      }

      const nextItems = reorderSortableListItems(
        items,
        reorder.sourceIndex,
        reorder.targetIndex,
      );

      onItemsChange?.(nextItems);
      onReorder?.({
        items: nextItems,
        sourceId: reorder.sourceId,
        targetId: reorder.targetId,
        sourceIndex: reorder.sourceIndex,
        targetIndex: reorder.targetIndex,
      });
    },
    [itemIds, items, onItemsChange, onReorder],
  );

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <ul
        aria-label={ariaLabel ?? listLabel}
        className={cn("m-0 w-full list-none p-0", className)}
        {...props}
      >
        {items.map((item, index) => {
          const id = getItemId(item);
          const label = getItemLabel(item, index);

          return (
            <SortableListItem
              disabled={disabled}
              getHandleLabel={getHandleLabel}
              handleClassName={handleClassName}
              id={id}
              index={index}
              item={item}
              itemClassName={itemClassName}
              key={id}
              label={label}
              renderItem={renderItem}
            />
          );
        })}
      </ul>
    </DragDropProvider>
  );
};

type SortableListItemProps<Item> = {
  item: Item;
  id: SortableListItemId;
  index: number;
  label: string;
  disabled: boolean;
  renderItem: (props: SortableListRenderItemProps<Item>) => React.ReactNode;
  itemClassName?: SortableListProps<Item>["itemClassName"];
  handleClassName?: string;
  getHandleLabel?: (item: Item, index: number) => string;
};

const SortableListItem = <Item,>({
  item,
  id,
  index,
  label,
  disabled,
  renderItem,
  itemClassName,
  handleClassName,
  getHandleLabel,
}: SortableListItemProps<Item>) => {
  const { ref, handleRef, isDragging, isDropping, isDragSource, isDropTarget } =
    useSortable({
      disabled,
      id,
      index,
    });

  const renderProps = React.useMemo<SortableListRenderItemProps<Item>>(
    () => ({
      item,
      id,
      index,
      isDragging,
      isDropping,
      isDragSource,
      isDropTarget,
    }),
    [id, index, isDragSource, isDragging, isDropping, isDropTarget, item],
  );

  const resolvedItemClassName =
    typeof itemClassName === "function"
      ? itemClassName(renderProps)
      : itemClassName;

  return (
    <li
      aria-label={label}
      className={cn(
        "flex min-h-12 items-center gap-2 border-border border-b bg-background px-1 py-2 transition-opacity",
        isDragging ? "opacity-60" : null,
        isDropTarget ? "bg-muted/40" : null,
        disabled ? "opacity-60" : null,
        resolvedItemClassName,
      )}
      data-drag-source={isDragSource ? "true" : undefined}
      data-dragging={isDragging ? "true" : undefined}
      data-drop-target={isDropTarget ? "true" : undefined}
      ref={ref}
    >
      <Button
        aria-label={getHandleLabel?.(item, index) ?? `Reorder ${label}`}
        className={cn(
          "h-8 w-8 shrink-0 cursor-grab text-muted-foreground hover:bg-transparent hover:text-foreground active:cursor-grabbing",
          disabled ? "cursor-not-allowed" : null,
          handleClassName,
        )}
        disabled={disabled}
        ref={(element) => {
          handleRef(element);
        }}
        size="icon"
        type="button"
        variant="ghost"
      >
        <GripVertical aria-hidden="true" className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1">{renderItem(renderProps)}</div>
    </li>
  );
};
